import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { env } from '../env.ts';

/**
 * HTTP client mirroring the integration test client's contract:
 *   - Same-origin in production (apiBaseUrl is '').
 *   - withCredentials: true so JSESSIONID + XSRF-TOKEN cookies travel.
 *   - On writes, read the XSRF-TOKEN cookie value and add it as the
 *     X-XSRF-TOKEN header. (Spring's CookieCsrfTokenRepository pattern.)
 *   - Doesn't auto-redirect on 401 — the auth interceptor in src/auth/
 *     subscribes to errors and opens the login modal.
 */
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

export const api: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
  validateStatus: () => true, // we deal with non-2xx ourselves
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (WRITE_METHODS.has(method)) {
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf) {
      config.headers.set('X-XSRF-TOKEN', xsrf);
    }
  }
  return config;
});

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

/** Throw on non-2xx — call this in tanstack-query queryFn / mutationFn. */
export async function jsonOrThrow<T>(method: string, url: string, data?: unknown): Promise<T> {
  const res = await api.request<T>({ method, url, data });
  if (res.status >= 200 && res.status < 300) return res.data;
  throw new HttpError(res.status, res.data, url);
}

export class HttpError extends Error {
  constructor(public status: number, public body: unknown, public url: string) {
    super(`${status} on ${url}`);
  }
  isAuthError(): boolean { return this.status === 401; }
  isForbidden(): boolean { return this.status === 403; }
}

// Convenience helpers — most callers won't need the raw `api` axios instance.
export const http = {
  get: <T>(url: string) => jsonOrThrow<T>('get', url),
  post: <T>(url: string, data?: unknown) => jsonOrThrow<T>('post', url, data),
  put: <T>(url: string, data?: unknown) => jsonOrThrow<T>('put', url, data),
  delete: <T>(url: string) => jsonOrThrow<T>('delete', url),
};

/**
 * Fire `cb` whenever the API returns 401 so the auth store can prompt re-auth
 * (e.g. after a mid-session expiry). Because the instance sets
 * `validateStatus: () => true`, a 401 RESOLVES rather than rejects — so we must
 * inspect it on the fulfillment side. (The rejection handler is kept for the
 * rare transport-level error that still carries a 401 response.) Returning the
 * response unchanged preserves each caller's own status handling.
 */
export function onUnauthorized(cb: () => void): () => void {
  const id = api.interceptors.response.use(
    (res) => {
      if (res.status === 401) cb();
      return res;
    },
    (err: AxiosError) => {
      if (err.response?.status === 401) cb();
      return Promise.reject(err);
    },
  );
  return () => api.interceptors.response.eject(id);
}
